import { useRef, useEffect } from 'react';
import { EventType, IWindowEventObserver } from '../../services/window-event-service.ts';
import { useAppContext } from '../../components/app-context/app-context.tsx';

// Adapted original from https://usehooks.com/useEventListener/

/**
 * Hook for services.base.windowEventService.register / unregister.
 */
export const useWindowEventServiceListener = (
  eventType: EventType,
  observer: IWindowEventObserver,
  options: {
    /**
     * Whether the event listener should be attached or not.
     * Defaults to true.
     */
    enabled?: boolean;
  } = {}
) => {
  const { enabled = true } = options;
  const { appContext } = useAppContext();

  // Create a ref that stores handler
  const savedObserver = useRef(observer);

  // Update ref.current value if handler changes.
  // This allows our effect below to always get latest handler ...
  // ... without us needing to pass it in effect deps array ...
  // ... and potentially cause effect to re-run every render.
  useEffect(() => {
    savedObserver.current = observer;
  }, [observer]);

  useEffect(() => {
    // Create dummy observer that calls the observer stored in ref
    const dummyObserver: IWindowEventObserver = {
      listener: e => savedObserver.current.listener?.(e),
      onPageVisible: hiddenDuration => savedObserver.current.onPageVisible?.(hiddenDuration),
      onPageHidden: () => savedObserver.current.onPageHidden?.()
    };

    // Add event listener
    if (enabled) {
      appContext.windowEventService?.register(eventType, dummyObserver);
    }

    // Remove event listener on cleanup
    return () => {
      if (enabled) {
        appContext.windowEventService?.unregister(eventType, dummyObserver);
      }
    };
  }, [enabled, eventType, appContext]);
};
